import { Component, ChangeDetectionStrategy, ElementRef, ViewChild, inject, input, output, OnDestroy } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { FileUploaderComponent, FileUploaderDialogData } from '../file-uploader/file-uploader.component';
import { UploadType } from '../../models/upload-type.type';
import { DisplayMode } from '../../models/display-mode.type';

@Component({
  selector: 'xmed-file-upload-button',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './file-upload-button.component.html',
  styleUrl: './file-upload-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploadButtonComponent implements OnDestroy {
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly dialog = inject(MatDialog);

  patientId = input<number | null>(null);
  commentId = input<number | null>(null);
  tags = input<string[]>([]);
  uploadType = input<UploadType>('practice');
  iconButton = input(false);
  displayMode = input<DisplayMode>('bottomsheet');

  uploadComplete = output<boolean>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private bottomSheetRef: MatBottomSheetRef<FileUploaderComponent> | null = null;
  private dialogRefInstance: MatDialogRef<FileUploaderComponent> | null = null;
  private subscription: Subscription | null = null;

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.bottomSheetRef = null;
    this.dialogRefInstance = null;
  }

  filesLoaded(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (!files || files.length === 0) {
      return;
    }

    const data: FileUploaderDialogData = {
      files: files,
      tags: this.tags(),
      type: this.uploadType(),
      patientId: this.patientId(),
      commentId: this.commentId()
    };

    if (this.displayMode() === 'dialog') {
      this.openDialog(data);
    } else {
      this.openBottomSheet(data);
    }
  }

  private openBottomSheet(data: FileUploaderDialogData): void {
    this.bottomSheetRef = this.bottomSheet.open(FileUploaderComponent, {
      disableClose: true,
      data: data
    });

    this.subscription = this.bottomSheetRef.afterDismissed().subscribe(dismissed => {
      this.fileInput.nativeElement.value = '';
      this.uploadComplete.emit(dismissed ?? false);
    });
  }

  private openDialog(data: FileUploaderDialogData): void {
    this.dialogRefInstance = this.dialog.open(FileUploaderComponent, {
      disableClose: true,
      data: data,
      width: '80vw',
      maxWidth: '900px'
    });

    this.subscription = this.dialogRefInstance.afterClosed().subscribe(result => {
      this.fileInput.nativeElement.value = '';
      this.uploadComplete.emit(result ?? false);
    });
  }
}
